import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { isEqual } from "lodash";

import { SelectBuyerList } from "components/form_components/BuyerListSelect";
import { BuyerSelect } from "components/form_components/BuyerSelect";
import { BuyerTypeSelect } from "components/form_components/BuyerTypeSelect";

export type CognismBuyerFilterValue = {
  buyerGuids: string[];
  buyerListIds: string[];
  buyerCategoryIds: string[];
};

type Props = {
  value: CognismBuyerFilterValue;
  onChange: (value: CognismBuyerFilterValue) => void;
};

/**
 * Buyer filter for the contacts sidebar — mirrors the Notices 2.0 buyers group
 * (components/notices_v2/FilterForm.tsx): individual buyers, buyer lists, and buyer
 * types, with individual buyers and lists mutually exclusive. The pickers are
 * react-hook-form fields, so we host a local form here and sync its selections out
 * to the sidebar's plain value/onChange state.
 */
function CognismContactsBuyersFilter({ value, onChange }: Props) {
  const { control, watch, reset, getValues } = useForm<CognismBuyerFilterValue>({
    defaultValues: value,
  });
  // The last value reconciled in either direction — guards both effects from looping.
  const lastSynced = useRef<CognismBuyerFilterValue>(value);
  // Latest onChange in a ref so the watch subscription below isn't torn down and
  // recreated on every parent render (the drawer passes a fresh inline handler).
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Form edits → parent. A subscription keeps the effect deps stable across renders;
  // getValues() returns the fully-typed values (watch's callback arg is a partial).
  useEffect(() => {
    const subscription = watch(() => {
      const next = getValues();
      if (!isEqual(next, lastSynced.current)) {
        lastSynced.current = next;
        onChangeRef.current(next);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getValues]);

  // External replace (e.g. the drawer's "Clear all") → form; skipped for our own edits.
  useEffect(() => {
    if (!isEqual(value, lastSynced.current)) {
      lastSynced.current = value;
      reset(value);
    }
  }, [value, reset]);

  const buyersSelected = (watch("buyerGuids") ?? []).length > 0;
  const listsSelected = (watch("buyerListIds") ?? []).length > 0;

  return (
    <div aria-label="Buyer filters">
      <BuyerSelect
        label="Buyers"
        name="buyerGuids"
        mode="multiple"
        control={control}
        placeholder="Search buyers"
        disabled={listsSelected}
        allowClear
      />
      <SelectBuyerList
        label="Buyer lists"
        name="buyerListIds"
        mode="multiple"
        control={control}
        placeholder="Select..."
        disabled={buyersSelected}
        allowClear
      />
      <BuyerTypeSelect
        label="Buyer type"
        name="buyerCategoryIds"
        control={control}
        multiple
        placeholder="Select"
        allowClear
      />
    </div>
  );
}

export default CognismContactsBuyersFilter;
